import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import {
  Button,
  Checkbox,
  FormControlLabel,
  Popover,
  TextField,
  TextFieldProps,
} from '@mui/material';

import moment, { Moment } from 'moment';
import { DATE_DISPLAY_FORMAT } from 'pages/TrackAndTrace/config';

import * as React from 'react';

type Date = Moment | null;

interface Props {
  label: string;
  date: Date;
  onChange: (date: Date) => boolean;
  checked: boolean;
  size?: 'small' | 'medium';
  style?: React.CSSProperties;
}

const labelClassesSmall = {
  label: 'font-medium text-sm py-1',
  root: 'm-0',
};

const labelClasses = {
  root: 'm-0',
};

/**
 * Implements a date picker with a calendar
 * @returns
 */
export const DateInput: React.FC<Props> = (props) => {
  const { label, date, onChange, checked, size, style } = props;

  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLButtonElement | null>(null);

  const handleChange = (d: Date) => {
    const sanitized = moment(moment(d).format('L')) || null;
    const success = onChange(sanitized);
    if (success) {
      setOpen(false);
    }
  };

  const handleClick = () => {
    setOpen((o) => !o);
  };

  const handleCheckChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (!e.target.checked) {
      onChange(null);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="inline-block space-x-2 items-center" style={style}>
        <FormControlLabel
          label={label}
          classes={size === 'small' ? labelClassesSmall : labelClasses}
          control={<Checkbox size={size} checked={checked} onChange={handleCheckChange} />}
        />
        <Button
          aria-describedby="calendar"
          onClick={handleClick}
          ref={ref}
          variant="outlined"
          size="small"
          className="p-2 h-6 shrink-0"
        >
          <h4 className="whitespace-nowrap">
            {date ? moment(date).format(DATE_DISPLAY_FORMAT) : 'Select date'}
          </h4>
        </Button>
        <Popover id="calendar" open={open} anchorEl={ref.current} onClose={handleClick}>
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            onChange={handleChange}
            value={date}
            renderInput={(params: TextFieldProps) => <TextField {...params} />}
          />
        </Popover>
      </div>
    </LocalizationProvider>
  );
};
DateInput.displayName = 'DateInput';
